/**
 * cart-drawer-render-fix.js
 *
 * Fixes: after Add to Cart, the item is genuinely added (verified via
 * /cart.js) but doesn't appear in the drawer's [data-products] container
 * until a hard refresh.
 *
 * Confirmed root cause (read directly from theme.js): theme.CartDrawer
 * listens for two different events on `document` -
 *   - 'cart:open'        -> only opens the drawer, no re-render
 *   - 'ajaxProduct:added' -> calls cartForm.buildCart() AND opens the
 *                            drawer
 * Any add-to-cart code path that dispatches 'cart:open' (or nothing at
 * all) instead of 'ajaxProduct:added' after a successful /cart/add.js
 * call will open the drawer showing stale markup, exactly matching the
 * hard-refresh symptom. The silk-satin-bedsheet PDP's custom add-to-cart
 * handler was one such path (fixed directly in its section file), but
 * this watchdog is kept as a generic safety net for any other add-to-cart
 * entry point with the same gap.
 *
 * It wraps window.fetch and, shortly after any successful call to
 * /cart/add.js, dispatches the real 'ajaxProduct:added' event that
 * theme.js's own CartDrawer already listens for - not a guessed internal
 * API call.
 *
 * PERF (26 Sep 2026) - two changes, see the 26 Sep HAR analysis:
 *
 * 1. No more duplicate re-render. The watchdog used to fire after EVERY
 *    add, even when the theme had already refreshed the drawer itself
 *    (theme.js dispatches 'ajaxProduct:added' for the product form and
 *    'cart:build' for addSingleItem - upsells and the free gift). That
 *    meant one extra /cart?view=ajax + /cart.js round trip per add. It now
 *    only steps in when the theme did NOT refresh the drawer on its own
 *    (no 'ajaxProduct:added' / 'cart:build' event and no /cart?view=ajax
 *    fetch since the add started). The safety net for other add paths is
 *    unchanged.
 *    The old watchdog also OPENED the drawer after every add. Some adds
 *    (theme.cart.addSingleItem - e.g. free product / gift adds made while the
 *    drawer is closed) refresh the drawer via 'cart:build' but never open it,
 *    so the drawer - and the confetti "popper" that theme.js draws inside it
 *    when the cart reaches its first item - stayed hidden. So when the theme
 *    already refreshed the drawer but it is still closed, we now send
 *    'cart:open' (opens only, no second re-render), keeping the old visible
 *    behaviour without the duplicate refresh.
 *
 * 2. Drawer images load early and first. Cart item and "Frequently Bought
 *    Together" images are loading="lazy" and the drawer slides in from
 *    off-screen, so they were only requested once the drawer was open, at
 *    the lowest priority, and queued behind other traffic (20-27s on slow
 *    4G for 2-7KB images). Now:
 *      - when a shopper touches Add to Cart / an upsell button / the cart
 *        icon, or the drawer opens or re-renders, every image in the drawer
 *        is switched to eager + high priority;
 *      - a few seconds after the page is ready, the cart item images (small,
 *        and only present when the cart has items) are fetched in the
 *        background at normal priority, so a returning shopper's drawer
 *        opens with photos already downloaded.
 */
(function () {
  if (window.__cartDrawerRenderFix) return;
  window.__cartDrawerRenderFix = true;

  var WATCHDOG_DELAY_MS = 300;
  var WARM_WINDOW_MS = 15000;
  var BACKGROUND_WARM_DELAY_MS = 4000;
  var CART_ADD_URL_RE = /\/cart\/add(\.js)?(\?.*)?$/;
  var CART_RENDER_URL_RE = /\/cart\?(.*&)?view=ajax/;
  var TRIGGER_SELECTOR = '[data-add-to-cart], .add-to-cart, .sticky-add-to-cart, .upsell-add-btn, .upsell-show-options, .js-drawer-open-cart';

  var lastThemeRender = 0;
  var warmUntil = 0;
  var backgroundWarm = false;

  function now() { return Date.now(); }
  function markThemeRender() { lastThemeRender = now(); }
  function getDrawer() { return document.getElementById('CartDrawer'); }
  function isOpen(drawer) { return !!drawer && drawer.classList.contains('drawer--is-open'); }

  function warmImages(root, high) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.tagName === 'IMG' ? [root] : root.querySelectorAll('img');
    Array.prototype.forEach.call(imgs, function (img) {
      try {
        // Priority first, then loading - so the request starts with the right priority.
        if (high && img.getAttribute('fetchpriority') !== 'high') img.setAttribute('fetchpriority', 'high');
        if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      } catch (e) { /* never break the cart */ }
    });
  }

  function warmDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    warmUntil = now() + WARM_WINDOW_MS;
    warmImages(drawer, true);
  }

  function cartItems(drawer) {
    return drawer ? drawer.querySelector('[data-products]') : null;
  }

  function backgroundWarmCartItems() {
    backgroundWarm = true;
    warmImages(cartItems(getDrawer()), false);
  }

  function observeDrawer() {
    var drawer = getDrawer();
    if (!drawer || drawer.__cbImgObserver) return;
    drawer.__cbImgObserver = true;
    new MutationObserver(function (mutations) {
      var high = isOpen(drawer) || now() < warmUntil;
      mutations.forEach(function (m) {
        if (m.type === 'attributes') {
          if (m.target === drawer && isOpen(drawer)) warmImages(drawer, true);
          return;
        }
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return;
          if (high) {
            warmImages(n, true);
          } else if (backgroundWarm) {
            var items = cartItems(drawer);
            if (items && items.contains(n)) warmImages(n, false);
          }
        });
      });
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  // The theme's own "drawer is being refreshed" signals.
  document.addEventListener('ajaxProduct:added', markThemeRender, true);
  document.addEventListener('cart:build', markThemeRender, true);

  // Warm the drawer as early as possible.
  document.addEventListener('ajaxProduct:added', warmDrawer, true);
  document.addEventListener('cart:build', warmDrawer, true);
  document.addEventListener('cart:open', warmDrawer, true);
  ['pointerdown', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var t = e.target;
      if (t && t.closest && t.closest(TRIGGER_SELECTOR)) warmDrawer();
    }, { capture: true, passive: true });
  });

  function onReady() {
    observeDrawer();
    setTimeout(backgroundWarmCartItems, BACKGROUND_WARM_DELAY_MS);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  function notifyCartDrawer() {
    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
  }

  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartAdd = CART_ADD_URL_RE.test(url);
    if (CART_RENDER_URL_RE.test(url)) markThemeRender();
    var startedAt = now();
    var result = originalFetch.apply(this, arguments);
    if (isCartAdd) {
      warmDrawer();
      result.then(function (response) {
        if (response && response.ok) {
          setTimeout(function () {
            if (lastThemeRender >= startedAt) {
              // The theme already refreshed the drawer for this add - don't refresh it twice,
              // but make sure it is visible, as the old watchdog did.
              if (!isOpen(getDrawer())) document.dispatchEvent(new CustomEvent('cart:open'));
              return;
            }
            notifyCartDrawer();
          }, WATCHDOG_DELAY_MS);
        }
      }).catch(function () {
        /* network/theme error - nothing for the watchdog to do */
      });
    }
    return result;
  };
})();
